import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, GripVertical } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import QuestionEditor from './QuestionEditor';

interface Survey {
  id?: number;
  title: string;
  description?: string | null;
  is_active: boolean;
  is_onboarding: boolean;
  questions?: Question[];
}

interface Question {
  id?: number;
  title: string;
  type: 'radio' | 'checkbox' | 'text';
  required: boolean;
  order: number;
  options?: Option[];
}

interface Option {
  value: string;
  label: string;
  order: number;
}

interface SurveyModalProps {
  survey: Survey | null;
  onClose: () => void;
  onSuccess: () => void;
}

const SurveyModal: React.FC<SurveyModalProps> = ({ survey, onClose, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingSurvey, setLoadingSurvey] = useState(false);

  useEffect(() => {
    if (survey) {
      setTitle(survey.title || '');
      setDescription(survey.description || '');
      setIsActive(survey.is_active || false);
      setIsOnboarding(survey.is_onboarding || false);
      
      // Загружаем полные данные опроса с вопросами
      if (survey.id) {
        loadSurveyDetails(survey.id);
      } else {
        setQuestions(survey.questions || []);
      }
    }
  }, [survey]);

  const loadSurveyDetails = async (surveyId: number) => {
    try {
      setLoadingSurvey(true);
      const response = await api.get(`/surveys/${surveyId}`);
      if (response.data.success && response.data.survey) {
        const surveyData = response.data.survey;
        const loadedQuestions = surveyData.questions || [];
        console.log('Загружено вопросов:', loadedQuestions.length);
        setQuestions(loadedQuestions);
      } else {
        console.error('Опрос не найден или не содержит вопросов');
        toast.error('Ошибка загрузки опроса: опрос не найден');
      }
    } catch (error: any) {
      console.error('Ошибка загрузки опроса:', error);
      toast.error(error.response?.data?.message || 'Ошибка загрузки опроса');
    } finally {
      setLoadingSurvey(false);
    }
  };

  const handleAddQuestion = () => {
    setQuestions([
      ...questions,
      {
        title: '',
        type: 'radio',
        required: true,
        order: questions.length,
        options: []
      }
    ]);
  };

  const handleUpdateQuestion = (index: number, question: Question) => {
    const updated = [...questions];
    updated[index] = { ...question, order: index };
    setQuestions(updated);
  };

  const handleDeleteQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index).map((q, i) => ({ ...q, order: i })));
  };

  const handleMoveQuestion = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === questions.length - 1) return;

    const updated = [...questions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    updated[index].order = index;
    updated[newIndex].order = newIndex;
    setQuestions(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error('Название опроса обязательно');
      return;
    }

    if (questions.length === 0) {
      toast.error('Добавьте хотя бы один вопрос');
      return;
    }

    // Валидация вопросов
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.title.trim()) {
        toast.error(`Вопрос ${i + 1}: текст вопроса обязателен`);
        return;
      }
      if ((q.type === 'radio' || q.type === 'checkbox') && (!q.options || q.options.length === 0)) {
        toast.error(`Вопрос ${i + 1}: добавьте варианты ответов`);
        return;
      }
    }

    try {
      setLoading(true);
      const surveyData = {
        title: title.trim(),
        description: description.trim() || null,
        isActive,
        isOnboarding,
        questions: questions.map((q, index) => ({
          title: q.title.trim(),
          type: q.type,
          required: q.required,
          order: index,
          options: q.options || []
        }))
      };

      if (survey?.id) {
        await api.put(`/surveys/${survey.id}`, surveyData);
        toast.success('Опрос обновлен');
      } else {
        await api.post('/surveys', surveyData);
        toast.success('Опрос создан');
      }

      onSuccess();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка сохранения опроса');
    } finally {
      setLoading(false);
    }
  };

  if (loadingSurvey) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">
            {survey ? 'Редактировать опрос' : 'Создать опрос'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Название опроса *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Описание
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex space-x-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">Активен</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={isOnboarding}
                onChange={(e) => setIsOnboarding(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">Использовать для онбординга</span>
            </label>
          </div>

          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Вопросы</h3>
              <button
                type="button"
                onClick={handleAddQuestion}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-5 w-5" />
                <span>Добавить вопрос</span>
              </button>
            </div>

            {questions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Нет вопросов. Добавьте первый вопрос.
              </div>
            ) : (
              <div className="space-y-4">
                {questions.map((question, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <GripVertical className="h-5 w-5 text-gray-400" />
                        <span className="text-sm font-medium text-gray-700">
                          Вопрос {index + 1}
                        </span>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => handleMoveQuestion(index, 'up')}
                          disabled={index === 0}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveQuestion(index, 'down')}
                          disabled={index === questions.length - 1}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteQuestion(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    <QuestionEditor
                      question={question}
                      onChange={(updatedQuestion) => handleUpdateQuestion(index, updatedQuestion)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Сохранение...' : survey ? 'Сохранить' : 'Создать'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SurveyModal;

